import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import RenderInBody from 'components/shared/render_in_body';
import ModalContainer from 'components/shared/modal/modal_container';
import AIAlert from 'components/shared/AI/AlAlert';
import { Button, ButtonGroup, Icon } from 'components/ui';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import AISvg from 'components/shared/svg/AISvg';
import Loading from 'components/shared/Loading';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import MultipleChoiceBody from './MultipleChoiceBody';
import FreeResponseBody from './FreeResponseBody';
import QuestionOptions from './QuestionOptions';
import {
  GENERATED_QUESTION_TYPES,
  GENERATED_QUESTION_OPTIONS,
} from '../constants';

const ElementAssistantModal = ({ dismissCallback, position }) => {
  const { id: currentSectionId } = useSelector(
    state => state.editor.currentSection,
  );
  const lessonId = useSelector(state => state.editor.lessonId);
  const [generatedOptions, setGeneratedOptions] = useState([]);
  const [gptMessage, setGptMessage] = useState({});
  const [selectedQuestionType, setSelectedQuestionType] = useState(
    GENERATED_QUESTION_TYPES.MULTIPLE_CHOICE,
  );
  const [selectedAIOption, setSelectedAIOption] = useState(
    GENERATED_QUESTION_OPTIONS.QUESTION_AND_ANSWERS,
  );
  const [questionText, setQuestionText] = useState('');
  const [isErrorShown, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const back = () => {
    setGeneratedOptions([]);
    setGptMessage({});
    setIsGenerated(false);
    setQuestionText('');
  };

  const sendLLMFeedback = async (idx, score) => {
    const feedbackData = {
      response_id: generatedOptions[idx].responseId,
      score,
      score_type: 'binary',
      fragment: {
        text: generatedOptions[idx],
        score,
      },
    };

    try {
      await request.postJSON(
        routes.ELEMENT_ASSISTANT.FEEDBACK(lessonId),
        feedbackData,
      );
      return { feedbackSent: true, error: false };
    } catch (e) {
      console.error(e);
      return { feedbackSent: true, error: true };
    }
  };

  const generate = question => {
    setError(false);
    setIsLoading(true);

    let prevMessage = gptMessage;
    if (question !== questionText) {
      prevMessage = {};
      setQuestionText(question);
    }

    const questionData = {
      section_id: currentSectionId,
      question_type: selectedQuestionType,
      question_options: selectedAIOption,
      message: prevMessage,
      question,
    };

    const route =
      selectedQuestionType === GENERATED_QUESTION_TYPES.FREE_RESPONSE
        ? routes.ELEMENT_ASSISTANT.FREE_RESPONSE(lessonId)
        : routes.ELEMENT_ASSISTANT.MULTIPLE_CHOICE(lessonId);

    request
      .postJSON(route, questionData)
      .then(resp => {
        const { questions, message } = resp;

        setGeneratedOptions(questions);
        setGptMessage(message);
        setIsGenerated(true);
        setIsLoading(false);
      })
      .catch(e => {
        console.error(e);
        setError(true);
        setIsLoading(false);
      });
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="ElementAssistantModal-loading">
          <Loading loading />
        </div>
      );
    }

    if (
      isGenerated &&
      (selectedQuestionType === GENERATED_QUESTION_TYPES.MULTIPLE_CHOICE ||
        selectedQuestionType === GENERATED_QUESTION_TYPES.MULTIPLE_SELECT)
    )
      return (
        <MultipleChoiceBody
          dismissCallback={dismissCallback}
          position={position}
          selectedQuestionType={selectedQuestionType}
          selectedAIOption={selectedAIOption}
          generatedOptions={generatedOptions}
          setGeneratedOptions={setGeneratedOptions}
          generate={generate}
          back={back}
          questionText={questionText}
          sendLLMFeedback={sendLLMFeedback}
        />
      );

    if (
      isGenerated &&
      selectedQuestionType === GENERATED_QUESTION_TYPES.FREE_RESPONSE
    ) {
      return (
        <FreeResponseBody
          dismissCallback={dismissCallback}
          position={position}
          selectedQuestionType={selectedQuestionType}
          generatedOptions={generatedOptions}
          setGeneratedOptions={setGeneratedOptions}
          generate={generate}
          back={back}
          questionText={questionText}
          sendLLMFeedback={sendLLMFeedback}
        />
      );
    }

    return (
      <>
        <AIAlert tooltipText={t('actions.ai.question_assistant_tooltip')} />
        <QuestionOptions
          selectedQuestionType={selectedQuestionType}
          setSelectedQuestionType={setSelectedQuestionType}
          selectedAIOption={selectedAIOption}
          setSelectedAIOption={setSelectedAIOption}
          questionText={questionText}
          setQuestionText={setQuestionText}
        />
        <ButtonGroup alignEnd className="pb-32">
          <Button onClick={dismissCallback}>{t('common.cancel')}</Button>
          <Button
            customColor
            disabled={
              selectedAIOption === GENERATED_QUESTION_OPTIONS.ANSWERS_ONLY &&
              !questionText
            }
            data-testid="ElementAssistantModalNextButton"
            onClick={() => generate(questionText)}
          >
            {t('common.next')}
          </Button>
        </ButtonGroup>
      </>
    );
  };

  const titleText = () => {
    if (isGenerated) {
      if (selectedAIOption === 'questions_with_answers') {
        return t('lessons.editor.element_assistant.generated_description');
      }
      if (selectedAIOption === 'answers_only') {
        return t(
          'lessons.editor.element_assistant.generated_answers_only_description',
        );
      }
    }
    return t('lessons.editor.element_assistant.description');
  };

  return (
    <RenderInBody>
      <ModalContainer
        onEscape={dismissCallback}
        className="ElementAssistantModal ModalTakeover-background Modal-background"
      >
        <div className="Modal-container ElementAssistantModal-container">
          <div
            className="ModalTakeover-closeButton"
            aria-label={t('common.close')}
            role="button"
            tabIndex="0"
            onClick={dismissCallback}
            onKeyDown={e => e.key === 'Enter' && dismissCallback()}
          >
            <Icon name="remove" />
          </div>
          <div className="ElementAssistantModal-body">
            <div className="ElementAssistantModal-directions">
              <div>
                <Text variant={TEXT_VARIANTS.TITLE}>
                  <AISvg className="generate-svg" />
                  {t('lessons.editor.element_assistant.title')}
                </Text>
              </div>
              <div>
                <Text variant={TEXT_VARIANTS.BODY_LIGHT}>{titleText()}</Text>
              </div>
            </div>
            {renderBody()}
            {isErrorShown && (
              <div className="ErrorContainer ElementAssistantModal-errorMessage p-4 mt-10">
                {t('lessons.editor.element_assistant.error')}
              </div>
            )}
          </div>
        </div>
      </ModalContainer>
    </RenderInBody>
  );
};

export default ElementAssistantModal;
